import { Injectable, inject } from "@angular/core";
import {
  Router,
  NavigationStart,
  NavigationEnd,
  NavigationCancel,
} from "@angular/router";
import { LocationStrategy } from "@angular/common";
import Cookies from "js-cookie";

// @aa/common
import { AppConfigService, IAnalyticsContext } from "./app-config.service";

export interface IAnalyticsEvent {
  category: string;
  action: string;
  label?: string;
  value?: number;
}

export type CookiePref = "accepted" | "rejected";

interface CookiePrefWithDate {
  value: CookiePref;
  date: string | Date;
}

const COOKIE_PREF_KEY = "cookiePref";
const YEAR_IN_MILLISECONDS = 31556952000;

@Injectable({ providedIn: "root" })
export class AnalyticsService {
  private readonly appConfig = inject(AppConfigService);
  private readonly router = inject(Router);

  public readonly appOrigin: string;

  private start: NavigationStart | null = null;
  private startTime = 0;

  public cookiePref: CookiePref | null = null;

  // The GA script is injected at runtime and may never load (blocked, consent
  // denied), so window.gtag's ambient non-optional type over-promises.
  private readonly gtag: Gtag.Gtag | undefined = window.gtag;
  private gtagInitialised = false;

  private analyticsContext: IAnalyticsContext = {};

  constructor() {
    const location = inject(LocationStrategy);

    this.appOrigin = window.location.origin + location.getBaseHref();

    // listen to router events
    this.router.events.subscribe((val) => {
      if (val instanceof NavigationStart) {
        this.setNavigationStart(val);
      } else if (val instanceof NavigationCancel) {
        this.clearNavigationStart();
      } else if (val instanceof NavigationEnd) {
        this.trackPageView(val);
        this.clearNavigationStart();
      }
    });

    // listen to print events (feature-detect: matchMedia is absent in older /
    // non-DOM hosts; `typeof` is the honest check since lib.dom types it as a
    // required method).
    if (typeof window.matchMedia === "function") {
      const mediaQueryList = window.matchMedia("print");

      const callback = (mql: MediaQueryListEvent) => {
        if (!mql.matches) {
          this.trackPrintEvent();
        }
      };

      // Older Safari lacked MediaQueryList.addEventListener; lib.dom types it
      // as required, so `typeof` is the honest feature check.
      if (typeof mediaQueryList.addEventListener === "function") {
        mediaQueryList.addEventListener("change", callback);
      }
    }

    this.appConfig.analyticsContext$.subscribe((ctx) => {
      this.analyticsContext = ctx || {};
      this.initGoogleAnalytics();
    });

    window.onafterprint = () => this.trackPrintEvent();

    this.cookiePref = this.readCookiePref();
    this.updateGoogleAnalyticsConsent();

    this.initHotjar();
  }

  private readCookiePref(): CookiePref | null {
    try {
      const json = window.localStorage.getItem(COOKIE_PREF_KEY);
      if (!json) {
        return null;
      }

      const data: CookiePrefWithDate = JSON.parse(json);

      // check time remaining
      const timeRemaining = Date.now() - new Date(data.date).getTime();
      if (timeRemaining > YEAR_IN_MILLISECONDS) {
        console.log("CookiePref expired");
        return null;
      }

      return data.value;
    } catch {
      console.warn("Failed to read cookie preference");
      return null;
    }
  }

  get hasCookiePref(): boolean {
    return !!this.cookiePref;
  }

  public setCookiePref(cookiePref: CookiePref) {
    this.cookiePref = cookiePref;

    try {
      const data: CookiePrefWithDate = {
        value: cookiePref,
        date: new Date(),
      };
      window.localStorage.setItem(COOKIE_PREF_KEY, JSON.stringify(data));
    } catch {
      console.warn("Failed to save cookie preference");
    }

    this.updateGoogleAnalyticsConsent();
  }

  private setNavigationStart(value: NavigationStart) {
    this.start = value;
    this.startTime = new Date().getTime();
  }

  private clearNavigationStart() {
    this.start = null;
    this.startTime = 0;
  }

  private trackPageView(end: NavigationEnd) {
    if (!this.start) {
      return;
    }

    if (this.start.id !== end.id) {
      console.warn("Analytics: Navigation ID mismatch", this.start, end);
      return;
    }

    const url = end.urlAfterRedirects.split("?")[0];
    const duration = new Date().getTime() - this.startTime;

    if (this.gtag) {
      //https://developers.google.com/analytics/devguides/collection/gtagjs/pages#page_view_event
      this.gtag("event", "page_view", {
        page_location: this.appOrigin,
        page_path: url,
      });

      this.gtag("event", "timing_complete", {
        name: "load",
        value: duration,
        event_category: "Page Load",
      });
    }
  }

  public trackPrintEvent() {
    this.trackExportEvent("Print");
  }

  public trackExportEvent(
    action: "Print" | "Email" | "Pdf" | "Word" | "Html",
    label?: string,
  ) {
    this.trackEvent({
      category: "Export",
      action: action,
      label: label,
    });
  }

  public trackErrorEvent(name: string, message: string) {
    this.trackEvent({
      category: "Error",
      action: name,
      label: message,
    });
  }

  public trackOutboundLink(href: string) {
    if (typeof this.gtag === "function") {
      this.gtag("event", "Outgoing Links", {
        to: href,
      });
    }
  }

  public trackEvent(e: IAnalyticsEvent) {
    // log events to console in debug mode
    if (!this.appConfig.production) {
      console.log("Analytics Event", e);
    }

    // https://developers.google.com/analytics/devguides/collection/analyticsjs/events
    if (typeof this.gtag === "function") {
      const params = {
        event_category: e.category,
        ...(e.label ? { event_label: e.label } : {}),
        ...(e.value != undefined ? { value: e.value } : {}),
      };
      this.gtag("event", e.action, params);
      console.log("gtag event", e.action, params);
    }
  }

  private initHotjar(): boolean {
    if ((window as any)["hj"]) {
      return true;
    }

    const hjid = this.appConfig.hotjarId;
    if (!hjid) {
      return false;
    }

    // modified version of hotjar snippet with hjid injected
    // prettier-ignore
    (function (h, o, t, j, a, r) {
      // @ts-expect-error copy and paste js snippet
      // eslint-disable-next-line prefer-rest-params
      h.hj = h.hj || function () { (h.hj.q = h.hj.q || []).push(arguments); }; h._hjSettings = { hjid: hjid, hjsv: 6 }; a = o.getElementsByTagName('head')[0]; r = o.createElement('script'); r.async = 1; r.src = t + h._hjSettings.hjid + j + h._hjSettings.hjsv; a.appendChild(r);
    })(window, document, 'https://static.hotjar.com/c/hotjar-', '.js?sv=');
    return true;
  }

  // https://stackoverflow.com/a/65865934
  private updateGoogleAnalyticsConsent() {
    if (!this.gtag) {
      return;
    }

    const cookiesEnabled =
      this.appConfig.appName == "app" || this.cookiePref === "accepted";
    const consent = cookiesEnabled ? "granted" : "denied";

    this.gtag("consent", "update", {
      analytics_storage: consent,
    });

    console.log(`Analytics cookies: ${consent}`);

    // if rejected then delete all ga cookies
    if (!cookiesEnabled) {
      const options = { path: "/", domain: location.hostname };
      Cookies.remove("_ga", options);

      const cookieSuffix = this.appConfig.gaKey
        ? this.appConfig.gaKey.substring(2)
        : "";
      if (cookieSuffix) {
        Cookies.remove(`_ga_${cookieSuffix}`, options);
      }
    }
  }

  private initGoogleAnalytics(): boolean {
    if (!this.gtag) {
      console.warn("gtag not loaded");
      return false;
    }

    const measurementId = this.appConfig.gaKey;
    if (!measurementId) {
      console.warn("GTag measurement ID undefined");
      return false;
    }

    // The team/domain custom dimensions, refreshed from the latest context.
    const dimensions = {
      aa_team: this.analyticsContext.teamName,
      aa_domain: this.analyticsContext.domain,
    };

    // analyticsContext$ is a BehaviorSubject that emits null then
    // progressively-complete context, so this method runs several times during
    // startup. Configure the measurement exactly once (GA4's recommended
    // pattern, and it avoids re-running the setup), folding the dimensions into
    // that init so they are present from the first hit when context is already
    // available. Later emissions just refresh the dimensions via set.
    if (this.gtagInitialised) {
      this.gtag("set", dimensions);
      return true;
    }

    this.gtagInitialised = true;
    this.gtag("config", measurementId, {
      custom_map: {
        dimension1: "aa_team",
        dimension2: "aa_domain",
      },
      send_page_view: false,
      app_version: this.appConfig.version,
      app_name: this.appConfig.appName,
      ...dimensions,
    });

    return true;
  }
}
